/**
 * ImageCard — the web counterpart of the mobile `ImageCard` (mobile/src/navigation/home/ImageCard.tsx):
 * the one cover-image card used across the trips page (trips, and soon featured destinations + hotels).
 *
 * A photo fills the card with a soft bottom scrim (the Apple Photos treatment) and the title + secondary
 * text overlaid bottom-left in white. With no image it falls back to the brand placeholder: a diagonal
 * camel gradient with the mark bled off the bottom-right corner and themed (dark) text. `orientation`
 * sets the aspect ratio (landscape 16:9 / portrait 3:4 / square 1:1); `size` scales the overlay type.
 *
 * Named `ImageCard` to avoid the shadcn `Card` primitive. It renders a router `<Link>` when given a
 * route (`to`/`params`) so trip cards keep real anchor semantics, otherwise a `<button>` (`onClick`) —
 * the web analogue of the mobile card's `onPress`.
 */
import type { MouseEvent, ReactNode } from 'react';

import type { RoutePath } from '#routes.js';

import { cn } from '@elara/client-core/lib/utils';

import { Badge, Logo } from '#ui/index.js';
import { Link } from '#views/Router.js';

export type ImageCardOrientation = 'portrait' | 'landscape' | 'square';
export type ImageCardSize = 'large' | 'small';

interface ImageCardProps {
  title: string;
  secondaryText?: string;
  /** Cover image URL. Falls back to the brand placeholder when absent. */
  image?: string;
  orientation?: ImageCardOrientation;
  size?: ImageCardSize;
  /** Optional badge shown beside the title (e.g. "Demo"). */
  badge?: string;
  /** Desaturate the cover photo (e.g. past trips); recolours on hover. */
  desaturated?: boolean;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  /** When set the card is a router `<Link>` to this route. */
  to?: RoutePath;
  params?: Record<string, string>;
  /**
   * Plain anchor href for targets that aren't router routes (e.g. a hash-fragment guide-preview link).
   * Renders an `<a href>` so cmd/middle-click still opens a new tab; pair with `onClick` to handle the
   * normal click (e.g. `e.preventDefault()` + open the panel). Ignored when `to` is set.
   */
  href?: string;
}

const ASPECT: Record<ImageCardOrientation, string> = {
  landscape: 'aspect-[6/4]',
  portrait: 'aspect-[3/4]',
  square: 'aspect-square',
};

const TYPE: Record<ImageCardSize, { title: string; secondary: string; logo: number }> = {
  large: { title: 'text-lg', secondary: 'text-sm', logo: 200 },
  small: { title: 'text-sm', secondary: 'text-xs', logo: 120 },
};

export function ImageCard({
  title,
  secondaryText,
  image,
  orientation = 'landscape',
  size = 'large',
  badge,
  desaturated = false,
  className,
  onClick,
  to,
  params,
  href,
}: ImageCardProps) {
  const hasCover = Boolean(image);
  const type = TYPE[size];

  const rootClassName = cn(
    'group relative block w-full cursor-pointer overflow-hidden rounded-sm',
    ASPECT[orientation],
    hasCover ? 'bg-background-tertiary' : 'bg-gradient-to-br from-background-tertiary to-background-quaternary',
    className,
  );

  const content: ReactNode = (
    <>
      {/* Cover lives in its own layer so the desaturate filter never touches the text/badge. */}
      {hasCover && (
        <span
          className={cn(
            'pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat',
            // The hover affordance, for every card. It is deliberately a zoom on THIS layer rather than
            // a lift on the card root: a transform on the root promotes the card to a backdrop-filter
            // root, which makes the progressive-blur band below bleed over the title text, and a card
            // inside an overflow-scroll carousel has its rise clipped by the scroller anyway. Here the
            // transform is a sibling of the blur band rather than its ancestor, so neither happens.
            // One `transition` shorthand rather than `transition-transform` + `transition-[filter,opacity]`:
            // those are the same twMerge conflict group, so only the last survived and a desaturated card's
            // zoom snapped. The shorthand keeps each property its own timing (the zoom is slower than the
            // recolour), which a single `duration-*` could not. Note it animates `scale`, NOT `transform`:
            // Tailwind v4's `scale-*` sets the standalone CSS `scale` property (its `transition-transform`
            // hides this by listing transform/translate/scale/rotate). Animating another property here
            // means naming it here too.
            '[transition:scale_500ms_ease-out,filter_200ms_ease-out,opacity_200ms_ease-out]',
            'group-hover:scale-105',
            desaturated && 'sepia opacity-50 group-hover:sepia-0 group-hover:opacity-100',
          )}
          style={{ backgroundImage: `url(${image})` }}
          aria-hidden
        />
      )}

      {/* Progressive blur: a bottom band whose backdrop-blur is faded in by a gradient mask (clear at
          the top → opaque at the bottom), so the title stays readable on busy photos. */}
      {hasCover && (
        <span
          className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 backdrop-blur-md [mask-image:linear-gradient(to_bottom,transparent,black)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black)]"
          aria-hidden
        />
      )}

      {/* No cover yet — the brand mark bled off the bottom-right corner at low opacity. */}
      {!hasCover && (
        <span
          className="pointer-events-none absolute inset-0 flex items-end justify-end overflow-hidden opacity-10"
          aria-hidden
        >
          <Logo variant="symbol" width={type.logo} height={type.logo} className="translate-x-1/5 translate-y-1/5" />
        </span>
      )}

      {/* Bottom scrim so the overlaid text stays legible over a photo. */}
      {hasCover && (
        <span
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent via-35% to-transparent"
          aria-hidden
        />
      )}

      {/* Hover dim — the cue that reads at a glance, alongside the slower cover zoom. It sits above the
          photo, the blur band and the scrim but below the badge and title, so the image recedes while the
          text stays at full contrast. Rendered for placeholder cards too: they have no cover to zoom, so
          this is their only hover affordance. */}
      <span
        className="pointer-events-none absolute inset-0 bg-transparent transition-colors duration-200 ease-out group-hover:bg-black/15"
        aria-hidden
      />

      {/* Badge anchored top-left (e.g. "Demo"), mirroring the active-trip badge placement. */}
      {badge && (
        <div className="absolute left-0 top-0 p-4">
          <Badge variant="info">{badge}</Badge>
        </div>
      )}

      {/* Title + secondary, anchored bottom-left. White (+ shadow) over a photo; themed over the placeholder. */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col p-4">
        <div className="flex items-center gap-2 overflow-hidden">
          <h2
            className={cn(
              'line-clamp-2 font-display',
              type.title,
              hasCover ? 'text-white [text-shadow:0_1px_2px_rgb(0_0_0_/_0.1)]' : 'text-foreground',
            )}
          >
            {title}
          </h2>
        </div>
        {secondaryText && (
          <div
            className={cn(
              type.secondary,
              'text-pretty line-clamp-2',
              hasCover ? 'text-white/90 [text-shadow:0_1px_2px_rgb(0_0_0_/_0.1)]' : 'text-muted-foreground',
            )}
          >
            {secondaryText}
          </div>
        )}
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} params={params as never} onClick={onClick} aria-label={title} className={rootClassName}>
        {content}
      </Link>
    );
  }

  // Plain anchor for non-route targets (e.g. a hash-fragment guide-preview link): keeps real anchor
  // semantics (cmd/middle-click → new tab) while `onClick` handles the in-page open.
  if (href) {
    return (
      <a href={href} onClick={onClick} aria-label={title} className={rootClassName}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-label={title} className={cn(rootClassName, 'text-left')}>
      {content}
    </button>
  );
}
